import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'last_14_days', label: 'Last 14 days' },
  { id: 'last_28_days', label: 'Last 28 days' },
  { id: 'all_available', label: 'All available' },
  { id: 'custom', label: 'Custom' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SHORT_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatDateShort(d) {
  if (!d) return '';
  const day = d.getDate();
  const m = SHORT_MONTH_NAMES[d.getMonth()];
  const y = d.getFullYear();
  return `${day} ${m} ${y}`;
}

function toYMD(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYMD(str) {
  if (!str) return null;
  const parts = str.split('-');
  if (parts.length !== 3) return null;
  return new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
}

function isSameDay(d1, d2) {
  if (!d1 || !d2) return false;
  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}

function getPresetDates(presetId) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yest = new Date(today);
  yest.setDate(yest.getDate() - 1);

  if (presetId === 'today') {
    return { start: today, end: today };
  }
  if (presetId === 'yesterday') {
    return { start: yest, end: yest };
  }
  if (presetId === 'last_7_days') {
    const s = new Date(yest);
    s.setDate(s.getDate() - 6);
    return { start: s, end: yest };
  }
  if (presetId === 'last_14_days') {
    const s = new Date(yest);
    s.setDate(s.getDate() - 13);
    return { start: s, end: yest };
  }
  if (presetId === 'last_28_days') {
    const s = new Date(yest);
    s.setDate(s.getDate() - 27);
    return { start: s, end: yest };
  }
  if (presetId === 'all_available') {
    return { start: null, end: null };
  }
  return { start: today, end: today };
}

export default function DashboardDateFilter({ filter, onChange, loading }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, right: 0 });

  // Timezone display
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

  // Internal state for modal
  const [tempPreset, setTempPreset] = useState(filter.preset || 'all_available');
  const [tempStart, setTempStart] = useState(() => {
    if (filter.startDate) return parseYMD(filter.startDate);
    const { start } = getPresetDates(filter.preset || 'all_available');
    return start;
  });
  const [tempEnd, setTempEnd] = useState(() => {
    if (filter.endDate) return parseYMD(filter.endDate);
    const { end } = getPresetDates(filter.preset || 'all_available');
    return end;
  });

  // Calendar view month (left calendar month)
  const [viewDate, setViewDate] = useState(() => {
    if (filter.endDate) {
      const d = parseYMD(filter.endDate);
      const prev = new Date(d);
      prev.setMonth(prev.getMonth() - 1);
      return prev;
    }
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return d;
  });

  // Hover date for range preview
  const [hoverDate, setHoverDate] = useState(null);

  
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 8,
        right: Math.max(16, window.innerWidth - rect.right),
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      const handleResize = () => updatePosition();
      const handleScroll = (e) => {
        // If scroll originated inside the modal, do not close
        if (popoverRef.current && popoverRef.current.contains(e.target)) return;
        setIsOpen(false);
      };
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Sync state when filter prop updates or modal opens
  const openModal = () => {
    updatePosition();
    const preset = filter.preset || 'all_available';
    setTempPreset(preset);
    let s = filter.startDate ? parseYMD(filter.startDate) : null;
    let e = filter.endDate ? parseYMD(filter.endDate) : null;
    if (!s && !e && preset !== 'all_available') {
      const res = getPresetDates(preset);
      s = res.start;
      e = res.end;
    }
    setTempStart(s);
    setTempEnd(e);

    // Anchor calendars around end date or current date
    const anchor = e || new Date();
    const v = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    setViewDate(v);
    setIsOpen(true);
  };

  const handlePrevMonth = () => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const now = new Date();
  const isNextDisabled =
    viewDate.getFullYear() > now.getFullYear() ||
    (viewDate.getFullYear() === now.getFullYear() && viewDate.getMonth() >= now.getMonth());

  const handleNextMonth = () => {
    if (isNextDisabled) return;
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleSelectPreset = (presetId) => {
    setTempPreset(presetId);
    if (presetId === 'all_available') {
      setTempStart(null);
      setTempEnd(null);
    } else if (presetId !== 'custom') {
      const { start, end } = getPresetDates(presetId);
      setTempStart(start);
      setTempEnd(end);
      if (end) {
        setViewDate(new Date(end.getFullYear(), end.getMonth() - 1, 1));
      }
    }
  };

  const handleDayClick = (dayDate) => {
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    if (dayDate.getTime() > endOfToday.getTime()) return;
    setTempPreset('custom');
    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(dayDate);
      setTempEnd(null);
    } else if (tempStart && !tempEnd) {
      if (dayDate.getTime() < tempStart.getTime()) {
        setTempEnd(tempStart);
        setTempStart(dayDate);
      } else {
        setTempEnd(dayDate);
      }
    }
  };

  const handleUpdate = () => {
    if (tempPreset === 'all_available') {
      onChange({ preset: 'all_available', startDate: '', endDate: '' });
    } else if (tempPreset === 'custom') {
      const s = tempStart || tempEnd || new Date();
      const e = tempEnd || tempStart || new Date();
      const finalStart = s.getTime() <= e.getTime() ? s : e;
      const finalEnd = s.getTime() <= e.getTime() ? e : s;
      onChange({
        preset: 'custom',
        startDate: toYMD(finalStart),
        endDate: toYMD(finalEnd),
      });
    } else {
      const { start, end } = getPresetDates(tempPreset);
      onChange({
        preset: tempPreset,
        startDate: start ? toYMD(start) : '',
        endDate: end ? toYMD(end) : '',
      });
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  // Button text display
  let triggerText = 'All available';
  if (filter.preset && filter.preset !== 'all_available') {
    if (filter.startDate && filter.endDate) {
      const s = parseYMD(filter.startDate);
      const e = parseYMD(filter.endDate);
      if (isSameDay(s, e)) {
        triggerText = formatDateShort(s);
      } else {
        triggerText = `${formatDateShort(s)} - ${formatDateShort(e)}`;
      }
    } else {
      const found = PRESETS.find((p) => p.id === filter.preset);
      if (found) triggerText = found.label;
    }
  }

  // Footer label display
  let footerRangeText = 'All available';
  if (tempPreset === 'all_available') {
    footerRangeText = 'All available dates';
  } else if (tempStart && tempEnd) {
    if (isSameDay(tempStart, tempEnd)) {
      footerRangeText = `${formatDateShort(tempStart)} - ${formatDateShort(tempEnd)}`;
    } else {
      footerRangeText = `${formatDateShort(tempStart)} - ${formatDateShort(tempEnd)}`;
    }
  } else if (tempStart) {
    footerRangeText = `${formatDateShort(tempStart)} - Selecting end date...`;
  }

  // Render a single month calendar
  const renderMonth = (monthOffset) => {
    const targetDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + monthOffset, 1);
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    const monthName = MONTH_NAMES[month];

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startWeekday = new Date(year, month, 1).getDay(); // 0 is Sun

    const cells = [];
    // Blank days before the 1st
    for (let i = 0; i < startWeekday; i++) {
      cells.push({ key: `empty-${i}`, empty: true });
    }

    const now = new Date();
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    for (let d = 1; d <= daysInMonth; d++) {
      const current = new Date(year, month, d);
      const isStart = tempStart && isSameDay(current, tempStart);
      const isEnd = tempEnd && isSameDay(current, tempEnd);
      const isSingleDay = isStart && isEnd;

      let inRange = false;
      if (tempStart && tempEnd) {
        inRange = current.getTime() > tempStart.getTime() && current.getTime() < tempEnd.getTime();
      } else if (tempStart && !tempEnd && hoverDate) {
        const hStart = tempStart.getTime() <= hoverDate.getTime() ? tempStart : hoverDate;
        const hEnd = tempStart.getTime() <= hoverDate.getTime() ? hoverDate : tempStart;
        inRange = current.getTime() > hStart.getTime() && current.getTime() < hEnd.getTime();
      }

      const isCurrentToday = isSameDay(current, todayDate);
      const isFuture = current.getTime() > endOfToday.getTime();

      cells.push({
        key: `day-${d}`,
        empty: false,
        date: current,
        dayNum: d,
        isStart,
        isEnd,
        isSingleDay,
        inRange,
        isCurrentToday,
        isFuture,
      });
    }

    return (
      <div className="dp-month-box">
        <div className="dp-month-title">{monthName} {year}</div>
        <div className="dp-weekdays-row">
          {WEEKDAYS.map((w) => (
            <span key={w} className="dp-weekday">{w}</span>
          ))}
        </div>
        <div className="dp-days-grid">
          {cells.map((cell) => {
            if (cell.empty) {
              return <div key={cell.key} className="dp-day-cell dp-day-empty" />;
            }
            let cellClasses = 'dp-day-cell';
            if (cell.isStart && cell.isEnd) cellClasses += ' dp-day-single';
            else if (cell.isStart) cellClasses += ' dp-day-start';
            else if (cell.isEnd) cellClasses += ' dp-day-end';
            else if (cell.inRange) cellClasses += ' dp-day-in-range';
            if (cell.isCurrentToday && !cell.isStart && !cell.isEnd) cellClasses += ' dp-day-today';
            if (cell.isFuture) cellClasses += ' dp-day-disabled';

            return (
              <div
                key={cell.key}
                className={cellClasses}
                onClick={() => !cell.isFuture && handleDayClick(cell.date)}
                onMouseEnter={() => !cell.isFuture && setHoverDate(cell.date)}
                onMouseLeave={() => setHoverDate(null)}
              >
                <span className="dp-day-text">{cell.dayNum}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="dp-root" ref={containerRef}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        className="dp-trigger-btn"
        onClick={isOpen ? () => setIsOpen(false) : openModal}
        disabled={loading}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <i className="fas fa-calendar-days dp-trigger-icon" />
        <span className="dp-trigger-text">{triggerText}</span>
        <i className="fas fa-chevron-down dp-trigger-arrow" />
      </button>

      {/* Popover Modal Dialog */}
      {isOpen &&
        createPortal(
          <div
            className="dp-popover fade-up"
            ref={popoverRef}
            style={{
              position: 'fixed',
              top: `${popoverPos.top}px`,
              right: `${popoverPos.right}px`,
              zIndex: 999999,
            }}
          >
          <div className="dp-body">
            {/* Left Sidebar Presets */}
            <div className="dp-sidebar">
              {PRESETS.map((p) => {
                const isSelected = tempPreset === p.id;
                return (
                  <label
                    key={p.id}
                    className={`dp-preset-item ${isSelected ? 'dp-preset-selected' : ''}`}
                    onClick={() => handleSelectPreset(p.id)}
                  >
                    <span className={`dp-radio-circle ${isSelected ? 'dp-radio-active' : ''}`}>
                      {isSelected && <span className="dp-radio-dot" />}
                    </span>
                    <span className="dp-preset-label">{p.label}</span>
                  </label>
                );
              })}
            </div>

            {/* Right Dual Calendar Section */}
            <div className="dp-calendar-section">
              {/* Month Navigation Arrows */}
              <div className="dp-nav-header">
                <button
                  type="button"
                  className="dp-nav-btn dp-nav-prev"
                  onClick={handlePrevMonth}
                  aria-label="Previous Month"
                >
                  <i className="fas fa-chevron-left" />
                </button>
                <button
                  type="button"
                  className={`dp-nav-btn dp-nav-next ${isNextDisabled ? 'dp-nav-disabled' : ''}`}
                  disabled={isNextDisabled}
                  onClick={handleNextMonth}
                  aria-label="Next Month"
                >
                  <i className="fas fa-chevron-right" />
                </button>
              </div>

              {/* Two Months Side-by-Side */}
              <div className="dp-months-wrap">
                {renderMonth(0)}
                {renderMonth(1)}
              </div>
            </div>
          </div>

          {/* Footer Bar */}
          <div className="dp-footer">
            <div className="dp-footer-info">
              <span className="dp-footer-range">{footerRangeText}</span>
              <span className="dp-footer-tz">Dates are shown in {userTimezone}</span>
            </div>
            <div className="dp-footer-actions">
              <button
                type="button"
                className="dp-btn-cancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="dp-btn-update"
                onClick={handleUpdate}
                disabled={loading}
              >
                Update
              </button>
            </div>
          </div>
        </div>,
          document.body
        )}
    </div>
  );
}
